import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  users: [],
  roles: [],
  permissions: [],
  vehicleTypes: [],
  countries: [],
  states: [],
  corporations: [],
  shops: [],
  cities: [],
  categories: [],
  subCategories: [],
  designations: [],
  particulars: [],
  sites: [],
  departments: [],
  loading: false,
  error: null,
};

export const helperSlice = createSlice({
  name: 'helper',
  initialState,
  reducers: {
    setUsers: (state, action) => {
      state.users = action.payload;
    },
    setRoles: (state, action) => {
      state.roles = action.payload;
    },
    setPermissions: (state, action) => {
      state.permissions = action.payload;
    },
    setVehicleTypes: (state, action) => {
      state.vehicleTypes = action.payload;
    },
    setCountries: (state, action) => {
      state.countries = action.payload;
    },
    setStates: (state, action) => {
      state.states = action.payload;
    },
    setCorporations: (state, action) => {
      state.corporations = action.payload;
    },
    setShops: (state, action) => {
      state.shops = action.payload;
    },
    setHelperCities: (state, action) => {
      state.cities = action.payload;
    },
    setHelperCategories: (state, action) => {
      state.categories = action.payload;
    },
    setHelperSubCategories: (state, action) => {
      state.subCategories = action.payload;
    },
    setHelperDesignations: (state, action) => {
      state.designations = action.payload;
    },
    setHelperParticulars: (state, action) => {
      state.particulars = action.payload;
    },
    setHelperSites: (state, action) => {
      state.sites = action.payload;
    },
    setHelperDepartments: (state, action) => {
      state.departments = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const {
  setUsers,
  setRoles,
  setPermissions,
  setVehicleTypes,
  setCountries,
  setStates,
  setCorporations,
  setShops,
  setHelperCities,
  setHelperCategories,
  setHelperSubCategories,
  setHelperDesignations,
  setHelperParticulars,
  setHelperSites,
  setHelperDepartments,
  setLoading,
  setError,
} = helperSlice.actions;

// Helper: Users
export const getHelperUsers = (search = '', role_id = '', stateId = '') => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const params = {};
    if (search) params.search = search;
    if (role_id) params.role_id = role_id;
    if (stateId) params.stateId = stateId;
    const response = await axiosInstance.get('/helper/users', { params });
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setUsers(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch users';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch users';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: Roles
export const getHelperRoles = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    let response;
    try {
      response = await axiosInstance.get('/helper/roles');
    } catch {
      response = await axiosInstance.get('/admin/helper/roles');
    }
    if (response.data && response.data.status === true) {
      const data = response.data.data?.result || response.data.data || [];
      dispatch(setRoles(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch roles';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch roles';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: Permissions
export const getHelperPermissions = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const response = await axiosInstance.get('/helper/permissions');
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setPermissions(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch permissions';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch permissions';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: Vehicle Types
export const getHelperVehicleTypes = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const response = await axiosInstance.get('/helper/vehicle-types');
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setVehicleTypes(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch vehicle types';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch vehicle types';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: Countries
export const getHelperCountries = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const response = await axiosInstance.get('/helper/countries');
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setCountries(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch countries';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch countries';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: States
export const getHelperStates = (countryId) => async (dispatch) => {
  if (!countryId) return { status: false, message: 'Country ID is required' };
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const response = await axiosInstance.get('/helper/states', {
      params: { countryId },
    });
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setStates(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch states';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch states';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: Corporations
export const getHelperCorporations = (stateId, city_id) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const params = {};
    if (typeof stateId === 'object' && stateId !== null) {
      if (stateId.stateId) params.stateId = stateId.stateId;
      const cId = stateId.city_id || stateId.cityId;
      if (cId) params.city_id = typeof cId === 'object' ? (cId.id || cId._id) : cId;
    } else {
      if (stateId) params.stateId = typeof stateId === 'object' ? (stateId.id || stateId._id) : stateId;
      if (city_id) params.city_id = typeof city_id === 'object' ? (city_id.id || city_id._id) : city_id;
    }
    const response = await axiosInstance.get('/helper/corporations', { params });
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setCorporations(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch corporations';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch corporations';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: Shops
export const getHelperShops = (corporationId, stateId, userId) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const params = {};
    if (corporationId) params.corporation_id = corporationId;
    if (stateId) params.stateId = stateId;
    if (userId) params.user_id = userId;
    const response = await axiosInstance.get('/helper/shops', { params });
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setShops(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch shops';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch shops';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: Cities
export const getHelperCities = (stateId) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const params = {};
    if (stateId) params.stateId = stateId;
    const response = await axiosInstance.get('/helper/cities', { params });
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setHelperCities(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch cities';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch cities';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: My Shops
export const getHelperMyShops = (params = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const response = await axiosInstance.get('/helper/my-shops', { params });
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setShops(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch my shops';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch my shops';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: My Cities
export const getHelperMyCities = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const response = await axiosInstance.get('/helper/my-cities');
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setHelperCities(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch my cities';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch my cities';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: Categories
export const getHelperCategories = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const response = await axiosInstance.get('/helper/categories');
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setHelperCategories(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch categories';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch categories';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: Sub Categories
export const getHelperSubCategories = (categoryId) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const params = {};
    if (categoryId) {
      params.category_id = typeof categoryId === 'object' ? (categoryId.id || categoryId._id) : categoryId;
    }
    const response = await axiosInstance.get('/helper/sub-categories', { params });
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setHelperSubCategories(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch sub categories';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch sub categories';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: Designations
export const getHelperDesignations = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const response = await axiosInstance.get('/helper/designations');
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setHelperDesignations(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch designations';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch designations';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Helper: Particulars
export const getHelperParticulars = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const response = await axiosInstance.get('/helper/particulars');
    if (response.data && response.data.status === true) {
      const data = response.data.data || [];
      dispatch(setHelperParticulars(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch particulars';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch particulars';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get Sites Helper List
export const getHelperSites = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    let response;
    try {
      response = await axiosInstance.get('/helper/sites');
    } catch {
      response = await axiosInstance.get('/admin/helper/sites');
    }
    if (response.data && response.data.status === true) {
      const data = response.data.data?.result || response.data.data || [];
      dispatch(setHelperSites(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch sites';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch sites';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Get Departments Helper List
export const getHelperDepartments = () => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    let response;
    try {
      response = await axiosInstance.get('/helper/departments');
    } catch {
      response = await axiosInstance.get('/admin/helper/departments');
    }
    if (response.data && response.data.status === true) {
      const data = response.data.data?.result || response.data.data || [];
      dispatch(setHelperDepartments(data));
      return { status: true, data };
    } else {
      const errMsg = response.data?.message || 'Failed to fetch departments';
      dispatch(setError(errMsg));
      return { status: false, message: errMsg };
    }
  } catch (err) {
    const errMsg = err.response?.data?.message || err.message || 'Failed to fetch departments';
    dispatch(setError(errMsg));
    return { status: false, message: errMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

export default helperSlice.reducer;
